/* Скелетоны экранов: серые плашки на местах, где сейчас появятся данные.

   Правило: скелетон повторяет каркас конкретного экрана — те же карточки,
   та же сетка метрик, столько же строк в списке. Общей заставки-логотипа на
   разделах больше нет: она стирает экран целиком, и переход читается как
   перезагрузка. Логотип остался только на первом старте (Splash в App).

   Каркас (сетки, подложки, канбан-колонки) рисуем настоящий — он не зависит
   от ответа сервера. Плашками закрываем только то, что придёт из API. */

import { List, ListItem } from "konsta/react";
import React from "react";
import { Ion } from "./icons.jsx";
import { Sep, StageBar } from "./ui.jsx";

/* ── Кирпичики ── */

/** Плашка на месте строки текста. `light` — для тёмных подложек */
export const Line = ({ w = "100%", h = 12, light = false, className = "", style }) => (
  <div className={`${light ? "skel-light" : "skel"} ${className}`} style={{ width: w, height: h, ...style }} />
);

export const Circle = ({ size = 38, light = false }) => (
  <div className={`${light ? "skel-light" : "skel"} shrink-0`} style={{ width: size, height: size, borderRadius: "50%" }} />
);

const Tile = ({ size = 29 }) => <div className="skel shrink-0" style={{ width: size, height: size, borderRadius: 7 }} />;

/** Плитка метрики: надпись сверху, число под ней — как <Metric> */
const MetricCell = () => (
  <div className="card-tg min-w-0 px-2.5 py-2" style={{ borderRadius: 14 }}>
    <Line w="60%" h={8} />
    <Line w="80%" h={13} className="mt-1.5" />
  </div>
);

/** Сетка метрик — повторяет <MetricGrid> */
export const SkelMetrics = ({ cols = 2, count = cols, className = "pt-1" }) => (
  <div className={`grid gap-2 px-4 ${cols === 3 ? "grid-cols-3" : "grid-cols-2"} ${className}`}>
    {Array.from({ length: count }, (_, i) => <MetricCell key={i} />)}
  </div>
);

/** Карточка со строками через разделитель — журнал, лента, алерты */
export const SkelRows = ({ rows = 4, avatar = false, right = true, lines = 2, className = "px-4" }) => (
  <div className={className}>
    <div className="card-tg py-1">
      {Array.from({ length: rows }, (_, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <Sep /> : null}
          <div className="flex items-center gap-3 px-4 py-2.5">
            {avatar ? <Circle size={38} /> : null}
            <div className="min-w-0 flex-1">
              <Line w={`${72 - (i % 3) * 12}%`} h={12} />
              {lines > 1 ? <Line w={`${52 - (i % 2) * 10}%`} h={9} className="mt-1.5" /> : null}
            </div>
            {right ? (
              <div className="shrink-0 flex flex-col items-end gap-1.5">
                <Line w={64} h={12} />
                {lines > 1 ? <Line w={40} h={9} /> : null}
              </div>
            ) : null}
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

/** Inset-список Konsta: плитка слева, название, значение справа */
export const SkelList = ({ rows = 5 }) => (
  <List strong inset>
    {Array.from({ length: rows }, (_, i) => (
      <ListItem
        key={i}
        media={<Tile />}
        title={<Line w={`${64 - (i % 3) * 14}%`} h={12} />}
        after={<Line w={44} h={10} />}
      />
    ))}
  </List>
);

/** Канбан: колонки известны из настроек бизнеса, ждём только карточки в них */
export const SkelKanban = ({ columns = [], cards = 2 }) => (
  <div className="hscroll flex gap-3 px-4 pt-3 items-start">
    {(columns.length ? columns : [{}, {}, {}]).map((c, ci) => (
      <div key={c.key || ci} className="w-[248px] shrink-0" style={{ scrollSnapAlign: "start" }}>
        <div className="px-0.5">
          <div className="flex items-baseline justify-between gap-2">
            {c.title ? (
              <span className="text-[13px] font-semibold truncate">{c.title}</span>
            ) : (
              <Line w="45%" h={12} />
            )}
            <Line w={14} h={10} />
          </div>
          <StageBar color={c.color || "var(--tg-separator)"} />
        </div>
        <div className="mt-2.5 flex flex-col gap-2">
          {Array.from({ length: Math.max(1, cards - (ci % 2)) }, (_, i) => (
            <div key={i} className="card-tg px-3 py-2.5">
              <Line w="80%" h={12} />
              <Line w="55%" h={9} className="mt-1.5" />
              <div className="mt-2 flex items-center justify-between">
                <Line w={38} h={9} />
                <Line w={56} h={11} />
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

/* ── Экраны ── */

/** Обзор: баланс, burn/runway, прогноз, кэшфлоу, воронки, лента */
export const SkelOverview = () => (
  <>
    <div className="px-4 pt-2">
      <div className="card-hero overflow-hidden">
        <div className="px-4 pt-4 pb-3">
          <Line w={92} h={8} light />
          <Line w="62%" h={30} light className="mt-2" />
          <Line w="48%" h={11} light className="mt-3" />
        </div>
        {[0, 1].map((i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-2.5">
            <Circle size={38} light />
            <div className="min-w-0 flex-1">
              <Line w="45%" h={12} light />
              <Line w="65%" h={9} light className="mt-1.5" />
            </div>
            <Line w={70} h={12} light />
          </div>
        ))}
      </div>
    </div>

    {/* Burn и Runway — карточки темы, ждут только числа */}
    <div className="px-4 pt-2 grid grid-cols-2 gap-2">
      <div className="card-tg px-4 py-3">
        <Line w={38} h={8} />
        <Line w="60%" h={17} className="mt-2" />
      </div>
      <div className="card-tg px-4 py-3">
        <Line w={54} h={8} />
        <Line w="60%" h={17} className="mt-2" />
      </div>
    </div>

    {/* Твёрдый прогноз */}
    <div className="px-4 pt-2">
      <div className="card-tg px-4 py-3.5">
        <Line w={180} h={8} />
        <Line w="52%" h={24} className="mt-2.5" />
        <Line w={96} h={10} className="mt-2.5" />
      </div>
    </div>

    {/* Кэшфлоу за 3 месяца */}
    <div className="px-4 pt-2">
      <div className="card-tg px-4 py-3.5">
        <div className="flex items-baseline justify-between gap-3">
          <Line w={140} h={8} />
          <Line w={78} h={14} />
        </div>
        <Line w="100%" h={6} className="mt-2.5" style={{ borderRadius: 999 }} />
        <Line w="70%" h={9} className="mt-2" />
      </div>
    </div>

    {/* Воронки продаж */}
    <div className="px-4 pt-4 pb-1.5">
      <Line w={110} h={8} />
    </div>
    <div className="px-4 grid grid-cols-2 gap-2">
      {[0, 1].map((i) => (
        <div key={i} className="card-tg px-3.5 py-3">
          <Line w="70%" h={9} />
          <Line w="80%" h={16} className="mt-2" />
          <Line w="55%" h={9} className="mt-1.5" />
        </div>
      ))}
    </div>

    {/* Лента */}
    <div className="px-4 pt-4 pb-1.5">
      <Line w={54} h={8} />
    </div>
    <SkelRows rows={3} right={false} />
  </>
);

/** Финансы: метрики, категории, кнопки выгрузки, журнал операций */
export const SkelFinance = () => (
  <>
    <SkelMetrics cols={3} count={6} />

    <div className="px-4 pt-4 pb-1.5">
      <Line w={160} h={8} />
    </div>
    <div className="px-4">
      <div className="card-tg py-1">
        {[0, 1, 2].map((i) => (
          <React.Fragment key={i}>
            {i > 0 ? <Sep /> : null}
            <div className="px-4 py-2.5">
              <div className="flex items-center justify-between gap-3 mb-2">
                <Line w={`${52 - i * 8}%`} h={12} />
                <Line w={70} h={12} />
              </div>
              <Line w={`${90 - i * 22}%`} h={6} style={{ borderRadius: 999 }} />
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>

    <div className="px-4 pt-4 grid grid-cols-2 gap-2">
      {[0, 1].map((i) => (
        <div key={i} className="card-tg flex items-center justify-center py-3">
          <Line w={86} h={13} />
        </div>
      ))}
    </div>

    <div className="pt-2">
      <SkelRows rows={5} avatar />
    </div>
  </>
);

/** CRM, поток сделок: метрики, полоса здоровья, канбан по этапам */
export const SkelCrmDeals = ({ columns }) => (
  <>
    <SkelMetrics cols={3} />
    <div className="px-4 pt-3 flex items-center gap-2">
      <Line w={120} h={8} />
      <Line w="100%" h={6} className="flex-1" style={{ borderRadius: 999 }} />
      <Line w={62} h={11} />
    </div>
    <SkelKanban columns={columns} />
  </>
);

/** CRM, аккаунтинг: метрики портфеля и канбан по стадиям */
export const SkelCrmAccounting = ({ columns }) => (
  <>
    <SkelMetrics cols={3} />
    <SkelKanban columns={columns} />
  </>
);

/** CRM, задачи: секции «Просрочено · Сегодня · Дальше» */
/* Вкладки срочности рисуем настоящими по форме — их три и они не зависят от ответа */
export const SkelTasks = () => (
  <>
    <div className="px-4 pt-1 flex gap-2">
      {[0, 1, 2].map((i) => (
        <Line key={i} w="100%" h={34} style={{ borderRadius: 999 }} />
      ))}
    </div>
    <SkelRows rows={4} right={false} className="px-4 pt-3" />
  </>
);

/** Алерты: две вкладки-пилюли и список уведомлений */
export const SkelAlerts = () => (
  <>
    <div className="px-4 pt-1 flex gap-2">
      {[0, 1].map((i) => (
        <div key={i} className="flex-1 rounded-full py-2.5 flex items-center justify-center" style={{ background: "var(--tg-section-bg)" }}>
          <Line w={92} h={12} />
        </div>
      ))}
    </div>
    <div className="pt-3">
      <SkelRows rows={4} right={false} lines={3} />
    </div>
  </>
);

/** Карточка сделки и проекта: шапка, деньги, поля, фид */
export const SkelEntityCard = ({ metrics = 3 }) => (
  <>
    <div className="px-4 pt-1">
      <div className="card-tg px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="skel shrink-0" style={{ width: 30, height: 30, borderRadius: 6 }} />
          <div className="min-w-0 flex-1">
            <Line w="62%" h={17} />
            <Line w="40%" h={10} className="mt-1.5" />
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <Line w={92} h={30} style={{ borderRadius: 999 }} />
          <Line w="100%" h={30} className="flex-1" style={{ borderRadius: 999 }} />
        </div>
      </div>
    </div>

    <div className="px-4 pt-4 pb-1.5">
      <Line w={62} h={8} />
    </div>
    <SkelMetrics cols={3} count={metrics} className="" />

    <div className="px-4 pt-4 pb-1.5">
      <Line w={96} h={8} />
    </div>
    <div className="px-4">
      <div className="card-tg py-1">
        {[0, 1, 2, 3].map((i) => (
          <React.Fragment key={i}>
            {i > 0 ? <Sep /> : null}
            <div className="flex items-center justify-between gap-3 px-4 py-2.5">
              <Line w={`${28 + (i % 3) * 8}%`} h={11} />
              <Line w={`${34 - (i % 2) * 8}%`} h={12} />
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>

    <div className="px-4 pt-4 pb-1.5">
      <Line w={48} h={8} />
    </div>
    <SkelRows rows={2} right={false} />
  </>
);

/** Карточка разработки: заголовок с пилюлями, лента этапов, чек-лист */
export const SkelDevTask = () => (
  <>
    <div className="px-4 pt-1">
      <div className="card-tg px-4 py-3">
        <Line w="82%" h={17} />
        <Line w="55%" h={12} className="mt-2.5" style={{ borderRadius: 999 }} />
      </div>
    </div>
    <div className="hscroll flex gap-2 px-4 pt-3">
      {[74, 92, 66, 80].map((w, i) => (
        <Line key={i} w={w} h={30} style={{ borderRadius: 999, flexShrink: 0 }} />
      ))}
    </div>
    <div className="px-4 pt-4 pb-1.5">
      <Line w={72} h={8} />
    </div>
    <SkelRows rows={3} right={false} lines={1} />
  </>
);

/** Карточка встречи: шапка с длительностью и блоки саммери */
export const SkelMeeting = () => (
  <>
    <div className="px-4 pt-1">
      <div className="card-tg px-4 py-3.5">
        <Line w="70%" h={17} />
        <Line w="50%" h={10} className="mt-2" />
      </div>
    </div>
    {[0, 1].map((i) => (
      <div key={i}>
        <div className="px-4 pt-4 pb-1.5">
          <Line w={i ? 96 : 68} h={8} />
        </div>
        <div className="px-4">
          <div className="card-tg px-4 py-3 flex flex-col gap-2">
            <Line w="100%" h={11} />
            <Line w="94%" h={11} />
            <Line w="72%" h={11} />
          </div>
        </div>
      </div>
    ))}
  </>
);

/** Отчёты: дата-заголовок и карточки со сводкой */
export const SkelReports = () => (
  <>
    {[0, 1].map((s) => (
      <div key={s}>
        <div className="px-4 pt-4 pb-1.5">
          <Line w={s ? 120 : 92} h={8} />
        </div>
        <div className="px-4 flex flex-col gap-2">
          {Array.from({ length: 2 - s }, (_, i) => (
            <div key={i} className="card-tg px-4 py-3">
              <Line w="42%" h={13} />
              <Line w="100%" h={11} className="mt-2.5" />
              <Line w="80%" h={11} className="mt-1.5" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </>
);

/** Бизнесы: свёрнутые карточки с фокусом */
export const SkelBusinesses = () => (
  <div className="px-4 pt-1 flex flex-col gap-2">
    {[0, 1, 2].map((i) => (
      <div key={i} className="card-tg px-4 py-3">
        <div className="flex items-center gap-2.5">
          <Circle size={9} />
          <div className="min-w-0 flex-1">
            <Line w={`${46 + (i % 2) * 14}%`} h={15} />
            <Line w="34%" h={9} className="mt-1.5" />
          </div>
        </div>
        <Line w="88%" h={11} className="mt-2.5" />
      </div>
    ))}
  </div>
);

/** Форма записи: inset-список полей и кнопка сохранения */
export const SkelForm = ({ fields = 5 }) => (
  <>
    <List strong inset>
      {Array.from({ length: fields }, (_, i) => (
        <ListItem key={i} title={<Line w={`${44 + (i % 3) * 16}%`} h={13} />} />
      ))}
    </List>
    <div className="px-4 pt-2">
      <Line w="100%" h={44} style={{ borderRadius: 999 }} />
    </div>
  </>
);

/** Шорткат: карточка-объяснение и список действий */
export const SkelShortcut = () => (
  <>
    <div className="px-4 pt-1 pb-1">
      <div className="card-tg px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <Ion.flash size={20} style={{ color: "var(--tg-hint)", opacity: 0.5 }} />
          <Line w="52%" h={14} />
        </div>
        <div className="mt-2.5 flex flex-col gap-2">
          <Line w="100%" h={11} />
          <Line w="92%" h={11} />
          <Line w="64%" h={11} />
        </div>
      </div>
    </div>
    <SkelList rows={2} />
  </>
);
